import { fromBase64 } from "../../../utils/base64";
import { TDocumentData } from "../models";

const LINKED_VRS_DATA_ID = "linked_vrs";

export type TLinkedVrPerson = {
  person_id: string;
  verification_request_id: number;
  has_email: boolean;
};

export type TLinkedVrsData = {
  persons: TLinkedVrPerson[];
};

function normalizeLinkedVrPerson(value: unknown): TLinkedVrPerson | null {
  if (!value || typeof value !== "object") {
    return null;
  }

  const person = value as Partial<TLinkedVrPerson>;

  if (
    typeof person.person_id !== "string" ||
    typeof person.verification_request_id !== "number"
  ) {
    return null;
  }

  return {
    person_id: person.person_id,
    verification_request_id: person.verification_request_id,
    has_email: person.has_email === true,
  };
}

function normalizeLinkedVrsData(value: unknown): TLinkedVrsData | null {
  if (!value || typeof value !== "object") return null;

  const persons = (value as TLinkedVrsData).persons;
  if (!Array.isArray(persons)) return null;

  const normalizedPersons = persons
    .map(normalizeLinkedVrPerson)
    .filter((person): person is TLinkedVrPerson => person !== null);

  if (normalizedPersons.length !== persons.length) {
    return null;
  }

  return {
    persons: normalizedPersons,
  };
}

function parseLinkedVrsPayload(payload: unknown): TLinkedVrsData | null {
  return normalizeLinkedVrsData(payload);
}

export function parseLinkedVrs(data: TDocumentData[] | undefined): TLinkedVrsData | null {
  if (!Array.isArray(data) || data.length === 0) {
    return null;
  }

  const linkedVrsItem = data.find(
    (item) => item.type === "binary" && item.dataId === LINKED_VRS_DATA_ID
  );

  if (!linkedVrsItem) {
    return null;
  }

  const parsedFromField = parseLinkedVrsPayload(linkedVrsItem.data as unknown);
  if (parsedFromField) {
    return parsedFromField;
  }

  if (!linkedVrsItem.dataBase64) {
    return null;
  }

  try {
    const decoded = fromBase64(linkedVrsItem.dataBase64);
    const parsed = JSON.parse(decoded);
    return parseLinkedVrsPayload(parsed);
  } catch {
    return null;
  }
}
